// live.ts — WebSocket client for /api/scans/{id}/live (ARCHITECTURE.md §7).
// Text frames carry JSON (status / progress / cameras / tracking / pong; §15
// adds server_tracking progress and tracking state, and live mode (§15.7)
// adds `live`, the GPU training session's stats); binary frames
// carry a 4-byte ASCII tag plus payload: "SNAP" + full OSPL or "DELT" + OSPD.
// The client pings every 20 s, and reconnects with capped exponential backoff
// plus jitter until close() is called — the server re-sends the latest SNAP on
// every connect, so a reconnect needs no client-side resync.
//
// §7.1 compressed encoding: once the zstd decoder (livez.ts, loaded on
// demand) is ready the client connects with ?enc=zstd1&zd=<JSON dictionary id>,
// and the server then sends every frame binary: "JSNZ" + zstd JSON or
// "SPQZ" + a zstd OSPQ quantized splat diff (splat/quant.ts). They are
// unwrapped into the same handler calls as the plain frames. If the decoder
// fails to load, or the server holds another dictionary, the plain frames
// arrive instead, and both kinds are always understood.

import type { Intrinsics, Pose, Scan } from './api';
import { urls } from './api';
import { parseOSPD, parseOSPL } from './splat/format';
import type { SplatCloud, SplatDelta } from './splat/format';
import { QDecoder } from './splat/quant';
import type { Unzstd } from './livez';

export interface ProgressMessage {
  type: 'progress';
  phase: string;
  iter: number;
  total_iters: number;
  loss: number;
  psnr: number;
  count: number;
  message: string;
  // §15.4.1 server_tracking progress (server-mode capture): frames tracked,
  // keyframes made, how far the pipeline lags the recorder (lag_s), frames
  // skipped to keep up and the media time reached (video_s). It carries no
  // iter/total_iters/loss/psnr/count, and `message` only now and then
  // (e.g. "preview seed 3: 41234 gaussians").
  frames?: number;
  keyframes?: number;
  lag_s?: number;
  skipped?: number;
  video_s?: number;
}

// TrackingMessage is the server video tracker's state (§15.4), the
// server-mode twin of a FrameResultJSON's state/pose_source/markers/quality.
// §15.4.1 adds the frame's clock (t_ms on the recorder clock, pts_s in the
// video) and pose, so a client can follow the trajectory.
export interface TrackingMessage {
  type: 'tracking';
  state: 'initializing' | 'tracking' | 'lost' | string;
  pose_source: string;
  markers: number[] | null;
  quality: number;
  frame?: number;
  t_ms?: number;
  pts_s?: number;
  have_pose?: boolean;
  pose?: Pose;
  keyframe?: boolean;
  keyframe_index?: number;
  keyframe_count?: number;
  tracked_points?: number;
  process_ms?: number;
}

// LivePhase is the live-mode session's state (§15.7): waiting for tracked
// frames, training, handing the model to refinement, done, or off (live was
// asked for but this server cannot do it; `message` says why).
export type LivePhase = 'waiting' | 'training' | 'handoff' | 'done' | 'off';

// LiveMessage is live mode's ~2 Hz stats frame: the GPU session training a
// splat model while a server-mode capture streams in. Only type and phase
// are guaranteed; unknown fields are ignored. Its splats arrive as ordinary
// SNAP/DELT (or SPQZ) cloud frames.
export interface LiveMessage {
  type: 'live';
  phase: LivePhase | string;
  frames?: number; // tracked frames the live session has seen
  keyframes?: number; // keyframes (views) trained on
  iter?: number; // training iterations so far
  gaussians?: number; // current model size
  loss?: number;
  psnr?: number; // when measured
  budget_ms?: number; // per-frame GPU budget
  train_ms?: number; // mean training time per frame
  overruns?: number; // frames that ran over budget
  snapshots?: number; // splat snapshots published
  hz?: number; // snapshot rate
  message?: string;
  model_key?: string; // on handoff: the stored model, e.g. "live/model.ospl"
}

export interface CamerasMessage {
  type: 'cameras';
  intrinsics: Intrinsics;
  poses: Pose[];
}

export interface LiveHandlers {
  onStatus?: (scan: Scan) => void;
  onProgress?: (p: ProgressMessage) => void;
  onCameras?: (c: CamerasMessage) => void;
  onTracking?: (t: TrackingMessage) => void;
  onLive?: (m: LiveMessage) => void;
  onSnapshot?: (cloud: SplatCloud) => void;
  onDelta?: (delta: SplatDelta) => void;
  onConnection?: (connected: boolean) => void;
}

export const PING_INTERVAL_MS = 20000;
export const BACKOFF_MIN_MS = 500;
export const BACKOFF_MAX_MS = 15000;
// How long a connect waits for the zstd decoder before going plain.
export const ZSTD_LOAD_TIMEOUT_MS = 4000;

interface Zstd {
  unzstd: Unzstd;
  encoding: string;
  token: string;
}

// loadZstd loads livez.ts and its decoder, or resolves null (logged) when
// that fails or takes longer than ZSTD_LOAD_TIMEOUT_MS.
function loadZstd(): Promise<Zstd | null> {
  const load = import('./livez').then(async (m) => ({ unzstd: await m.loadUnzstd(), encoding: m.ENCODING, token: m.DICT_TOKEN }));
  const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), ZSTD_LOAD_TIMEOUT_MS));
  return Promise.race([load, timeout])
    .then((z) => {
      if (!z) console.warn('[openscan/live] zstd decoder still loading; connecting uncompressed');
      return z;
    })
    .catch((err) => {
      console.warn('[openscan/live] zstd decoder unavailable; connecting uncompressed', err);
      return null;
    });
}

// liveURL adds the compressed-encoding parameters to the /live URL.
export function liveURL(base: string, z: { encoding: string; token: string } | null): string {
  if (!z) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}enc=${encodeURIComponent(z.encoding)}&zd=${encodeURIComponent(z.token)}`;
}

// backoffDelay is the reconnect delay for the n-th consecutive failure.
export function backoffDelay(attempt: number, rand: () => number = Math.random): number {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** Math.max(0, attempt));
  return Math.round(base * (0.75 + 0.5 * rand()));
}

// splitBinaryFrame returns the 4-byte tag and the payload view.
export function splitBinaryFrame(buf: ArrayBuffer): { tag: string; payload: Uint8Array } {
  const bytes = new Uint8Array(buf);
  if (bytes.length < 4) return { tag: '', payload: new Uint8Array(0) };
  return { tag: String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]), payload: bytes.subarray(4) };
}

// dispatchText routes one JSON message (a plain text frame or an unwrapped
// JSNZ frame) to its handler; unknown types are logged and dropped.
export function dispatchText(data: string, handlers: LiveHandlers): void {
  let msg: { type?: string; scan?: Scan } & Record<string, unknown>;
  try {
    msg = JSON.parse(data);
  } catch {
    console.warn('[openscan/live] non-JSON text frame ignored');
    return;
  }
  switch (msg.type) {
    case 'status':
      if (msg.scan) handlers.onStatus?.(msg.scan);
      break;
    case 'progress':
      handlers.onProgress?.(msg as unknown as ProgressMessage);
      break;
    case 'cameras':
      handlers.onCameras?.(msg as unknown as CamerasMessage);
      break;
    case 'tracking':
      handlers.onTracking?.(msg as unknown as TrackingMessage);
      break;
    case 'live':
      if (typeof msg.phase !== 'string') {
        console.warn('[openscan/live] live message without a phase ignored');
        break;
      }
      if (msg.phase !== 'training' && msg.phase !== 'waiting') console.info(`[openscan/live] live phase ${msg.phase}${msg.message ? `: ${msg.message}` : ''}`);
      handlers.onLive?.(msg as unknown as LiveMessage);
      break;
    case 'pong':
      break;
    default:
      console.debug('[openscan/live] unknown message type', msg.type);
  }
}

export class LiveClient {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private closed = false;
  private pingTimer: number | undefined;
  private retryTimer: number | undefined;
  private zstd: Zstd | null = null;
  private zstdTried = false;
  private q = new QDecoder();
  // Wire bytes of splat frames (both encodings), for the console summary.
  private splatBytes = 0;
  private splatFrames = 0;

  constructor(readonly scanId: string, private handlers: LiveHandlers) {
    this.connect();
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  setHandlers(h: LiveHandlers): void {
    this.handlers = h;
  }

  private connect(): void {
    if (this.closed) return;
    if (!this.zstdTried) {
      // First connect: load the decoder (or give up on it) before dialing,
      // so the very first snapshot already arrives compressed.
      this.zstdTried = true;
      void loadZstd().then((z) => {
        this.zstd = z;
        this.connect();
      });
      return;
    }
    const url = liveURL(urls.live(this.scanId), this.zstd);
    console.info(`[openscan/live] connecting ${url.split('?')[0]} (attempt ${this.attempt + 1}, ${this.zstd ? 'zstd' : 'plain'})`);
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.warn('[openscan/live] WebSocket constructor failed', err);
      this.scheduleReconnect();
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      console.info(`[openscan/live] connected scan=${this.scanId}`);
      this.q.reset(); // the server starts every connection with a snapshot
      this.attempt = 0;
      this.handlers.onConnection?.(true);
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), PING_INTERVAL_MS);
    };
    ws.onmessage = (ev: MessageEvent) => this.handleMessage(ev.data);
    ws.onerror = () => console.warn(`[openscan/live] socket error scan=${this.scanId}`);
    ws.onclose = (ev: CloseEvent) => {
      console.info(`[openscan/live] closed code=${ev.code} reason=${ev.reason || '-'}`);
      window.clearInterval(this.pingTimer);
      this.handlers.onConnection?.(false);
      if (this.ws === ws) this.ws = null;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const delay = backoffDelay(this.attempt++);
    console.info(`[openscan/live] reconnecting in ${delay} ms`);
    window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => this.connect(), delay);
  }

  private send(msg: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private handleMessage(data: unknown): void {
    if (typeof data === 'string') {
      this.handleText(data);
      return;
    }
    if (!(data instanceof ArrayBuffer)) return;
    const { tag, payload } = splitBinaryFrame(data);
    try {
      if (tag === 'JSNZ' || tag === 'SPQZ') {
        if (!this.zstd) throw new Error('compressed frame without a decoder');
        if (tag === 'JSNZ') {
          this.handleText(this.zstd.unzstd.json(payload));
          return;
        }
        this.countSplatFrame(data.byteLength);
        const raw = this.zstd.unzstd.splat(payload);
        const { snapshot, delta } = this.q.apply(raw);
        if (snapshot) {
          console.debug(`[openscan/live] SPQZ reset ${snapshot.count} gaussians (${payload.length} B, ${raw.length} B OSPQ)`);
          this.handlers.onSnapshot?.(snapshot);
        } else if (delta) {
          console.debug(`[openscan/live] SPQZ +${delta.updated.count} -${delta.removed.length} (${payload.length} B, ${raw.length} B OSPQ)`);
          this.handlers.onDelta?.(delta);
        }
      } else if (tag === 'SNAP') {
        this.countSplatFrame(data.byteLength);
        const cloud = parseOSPL(payload);
        console.debug(`[openscan/live] SNAP ${cloud.count} gaussians (${payload.length} B)`);
        this.handlers.onSnapshot?.(cloud);
      } else if (tag === 'DELT') {
        this.countSplatFrame(data.byteLength);
        const delta = parseOSPD(payload);
        console.debug(`[openscan/live] DELT +${delta.updated.count} -${delta.removed.length}`);
        this.handlers.onDelta?.(delta);
      } else {
        console.warn(`[openscan/live] unknown binary tag ${JSON.stringify(tag)}`);
      }
    } catch (err) {
      console.warn(`[openscan/live] bad ${tag} frame`, err);
    }
  }

  private countSplatFrame(bytes: number): void {
    this.splatBytes += bytes;
    if (++this.splatFrames % 50 === 0) {
      console.info(`[openscan/live] ${this.splatFrames} splat frames, ${(this.splatBytes / 1e6).toFixed(2)} MB received (${this.zstd ? 'zstd' : 'plain'})`);
    }
  }

  private handleText(data: string): void {
    dispatchText(data, this.handlers);
  }

  close(): void {
    this.closed = true;
    window.clearInterval(this.pingTimer);
    window.clearTimeout(this.retryTimer);
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close(1000, 'client closed');
    }
    console.info(`[openscan/live] client closed scan=${this.scanId}`);
  }
}
